import axios from "axios";
import Axios from "@/helpers/axios";
import StorageHelper from "@/helpers/storage";

export class BaseController {
  api = process.env.VUE_APP_URL_API;

  constructor() {
    this.request = Axios.getInstance({ api: this.api });

    // Always read the latest token from storage on each request
    this.request.interceptors.request.use(config => {
      const tokens = StorageHelper.loadState("tokens");
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }
      return config;
    });

    this.request.interceptors.response.use(
      response => response,
      async error => {
        const original = error.config;

        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;

          const tokens = StorageHelper.loadState("tokens");
          if (tokens?.refreshToken) {
            try {
              const res = await axios.post(
                `${this.api}/auth/refresh-tokens`,
                { refreshToken: tokens.refreshToken },
                { headers: { "Content-Type": "application/json" } }
              );

              const newTokens = res.data?.result ?? res.data;
              if (newTokens?.accessToken) {
                StorageHelper.saveState("tokens", newTokens);
                original.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                return this.request(original);
              }
            } catch (_) {
              // refresh failed — fall through to redirect
            }
          }

          StorageHelper.removeState("tokens");
          window.location.href = "/login";
        }

        throw error;
      }
    );
  }

  async _get(endpoint, options) {
    try {
      const res = await this.request.get(endpoint, options);
      return res.data;
    } catch (error) {
      throw {
        ...{ status: error.response?.status, statusText: error.response?.statusText },
        ...error.response?.data,
      };
    }
  }

  async _put(endpoint, data) {
    try {
      const res = await this.request.put(endpoint, data);
      return res.data;
    } catch (error) {
      throw {
        ...{ status: error.response?.status, statusText: error.response?.statusText },
        ...error.response?.data,
      };
    }
  }

  async _patch(endpoint, data) {
    try {
      const res = await this.request.patch(endpoint, data);
      return res.data;
    } catch (error) {
      throw {
        ...{ status: error.response?.status, statusText: error.response?.statusText },
        ...error.response?.data,
      };
    }
  }

  async _post(endpoint, data, options) {
    try {
      const res = await this.request.post(endpoint, data, options);
      return res.data;
    } catch (error) {
      throw {
        ...{ status: error.response?.status, statusText: error.response?.statusText },
        ...error.response?.data,
      };
    }
  }

  async _delete(endpoint) {
    try {
      const res = await this.request.delete(endpoint);
      return res.data;
    } catch (error) {
      throw {
        ...{ status: error.response?.status, statusText: error.response?.statusText },
        ...error.response?.data,
      };
    }
  }
}

const baseController = new BaseController();

export default baseController;
